import { createSelector } from 'reselect';
import { formatFullName } from '../../utils';

// Slice-level lookup shared with memoized selectors (e.g. getLastEditedBy),
// which receive the slices from createSelector rather than the whole state.
export const findContactInSlices = (contactsById, usersList, itemId) => {
  const contact = contactsById?.[itemId];
  if (contact) {
    return contact;
  }

  const user = usersList?.find(item => item.id === itemId);
  if (user) {
    return { ...user, isUser: true, firstName: user.firstName || user.email };
  }

  return null;
};

export const getContactItemById = ({ contacts, usersManagement }, itemId) =>
  findContactInSlices(contacts?.contactsById, usersManagement?.list, itemId);

const getContactsById = state => state.contacts?.contactsById;
const getUsersList = state => state.usersManagement?.list;

export const getContacts = createSelector(
  [getContactsById, getUsersList],
  (contactsById, usersList) => {
    const contactList = Object.values(contactsById ?? {});
    const userWithIsUserFlag =
      usersList?.map(user => ({
        ...user,
        isUser: true,
        firstName: user.firstName || user.email,
      })) ?? [];

    return [...contactList, ...userWithIsUserFlag]
      .map(contact => ({
        ...contact,
        name: formatFullName(contact),
      }))
      .sort(
        (a, b) => a.firstName?.localeCompare(b.firstName) || a.lastName?.localeCompare(b.lastName),
      );
  },
);

export const getContributors = ({ usersManagement, knowledge }) => {
  const contributorsFromAllKnowledgeItems = knowledge?.items
    ?.filter(item => item.contributors && item.contributors.length > 0)
    .flatMap(item => item.contributors);

  const uniqueContributorsMap = new Map();
  contributorsFromAllKnowledgeItems?.forEach(contributor => {
    // Use contributorId as key in the map to ensure uniqueness
    if (contributor.contributorId && !contributor.userId) {
      uniqueContributorsMap.set(contributor.contributorId, contributor);
    }
  });
  // Get the values from the map which are unique contributors
  const uniqueContributors = Array.from(uniqueContributorsMap.values());

  const users = usersManagement?.list ?? [];

  return [...users, ...uniqueContributors].map(user => ({
    ...user,
    firstName: user?.firstName || user?.displayName || user?.email || '',
  }));
};

// Memoized so repeat calls with the same users list return the same array
// reference — an unmemoized .map() here tripped react-redux's stabilityCheck.
export const getEmailsForContributors = (contributors = []) =>
  createSelector([getUsersList], usersList =>
    contributors.map(contributor => {
      const user = usersList?.find(user => user.id === contributor.userId);
      if (user) {
        return {
          ...contributor,
          email: user.email,
        };
      }
      return contributor;
    }),
  );

export const getUsers = ({ usersManagement }) => {
  return usersManagement?.list ?? [];
};
